import { z } from 'zod';

export const QUERY_CAPABILITY_SURFACES = ['report-list', 'user-account-list'] as const;
export type QueryCapabilitySurface = (typeof QUERY_CAPABILITY_SURFACES)[number];

export const QUERY_OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'isAnyOf',
  'notIn',
  'before',
  'after',
  'onOrBefore',
  'onOrAfter',
  'onDate',
] as const;
export type QueryOperator = (typeof QUERY_OPERATORS)[number];

export const QUERY_VALUE_KINDS = ['text', 'date', 'boolean', 'user-account-role'] as const;
export type QueryValueKind = (typeof QUERY_VALUE_KINDS)[number];

export const QUERY_OPERAND_KINDS = ['single', 'multiple', 'none'] as const;
export type QueryOperandKind = (typeof QUERY_OPERAND_KINDS)[number];

export const QUERY_TOTAL_COUNT_MODES = ['exact'] as const;
export type QueryTotalCountMode = (typeof QUERY_TOTAL_COUNT_MODES)[number];

export const QUERY_COMPOUND_SEMANTICS = ['and'] as const;
export type QueryCompoundSemantics = (typeof QUERY_COMPOUND_SEMANTICS)[number];

export const QUERY_INVALID_QUERY_REASONS = [
  'report/invalid-query-filter',
  'report/invalid-query-sort',
  'report/invalid-pagination',
  'user-account/invalid-query-filter',
  'user-account/invalid-query-sort',
  'user-account/invalid-pagination',
] as const;
export type QueryInvalidQueryReason = (typeof QUERY_INVALID_QUERY_REASONS)[number];

export interface QueryOperandConstraint {
  kind: QueryOperandKind;
  valueKind?: QueryValueKind;
  minimumLength?: number;
}

export interface QueryOperatorCapability {
  operator: QueryOperator;
  operand: QueryOperandConstraint;
}

export interface QueryFieldCapability {
  field: string;
  operators: QueryOperatorCapability[];
}

export interface QueryPaginationCapability {
  kind: 'page-number';
  pageSize: {
    minimum: number;
    default: number;
    maximum: number;
  };
  totalCount: QueryTotalCountMode;
}

export interface QueryCompoundCapability {
  semantics: QueryCompoundSemantics;
  maximumFilters: number;
}

export interface QueryUnsupportedCombination {
  field: string;
  operator: QueryOperator;
  reason: QueryInvalidQueryReason;
}

export interface QueryCapabilityFixture {
  surface: QueryCapabilitySurface;
  readSurface: string;
  fields: QueryFieldCapability[];
  sortableFields: string[];
  pagination: QueryPaginationCapability;
  compound: QueryCompoundCapability;
  unsupportedCombinations: QueryUnsupportedCombination[];
  invalidQueryReasons: QueryInvalidQueryReason[];
}

const QueryOperandConstraintSchema = z
  .object({
    kind: z.enum(QUERY_OPERAND_KINDS),
    valueKind: z.enum(QUERY_VALUE_KINDS).optional(),
    minimumLength: z.number().int().positive().optional(),
  })
  .strict();

const QueryOperatorCapabilitySchema = z
  .object({
    operator: z.enum(QUERY_OPERATORS),
    operand: QueryOperandConstraintSchema,
  })
  .strict();

const QueryFieldCapabilitySchema = z
  .object({
    field: z.string().min(1),
    operators: z.array(QueryOperatorCapabilitySchema).min(1),
  })
  .strict();

const QueryPaginationCapabilitySchema = z
  .object({
    kind: z.literal('page-number'),
    pageSize: z
      .object({
        minimum: z.number().int().positive(),
        default: z.number().int().positive(),
        maximum: z.number().int().positive(),
      })
      .strict(),
    totalCount: z.enum(QUERY_TOTAL_COUNT_MODES),
  })
  .strict();

const QueryCompoundCapabilitySchema = z
  .object({
    semantics: z.enum(QUERY_COMPOUND_SEMANTICS),
    maximumFilters: z.number().int().positive(),
  })
  .strict();

const QueryUnsupportedCombinationSchema = z
  .object({
    field: z.string().min(1),
    operator: z.enum(QUERY_OPERATORS),
    reason: z.enum(QUERY_INVALID_QUERY_REASONS),
  })
  .strict();

const QueryCapabilityFixtureSchema = z
  .object({
    surface: z.enum(QUERY_CAPABILITY_SURFACES),
    readSurface: z.string().min(1),
    fields: z.array(QueryFieldCapabilitySchema).min(1),
    sortableFields: z.array(z.string().min(1)),
    pagination: QueryPaginationCapabilitySchema,
    compound: QueryCompoundCapabilitySchema,
    unsupportedCombinations: z.array(QueryUnsupportedCombinationSchema),
    invalidQueryReasons: z.array(z.enum(QUERY_INVALID_QUERY_REASONS)).min(1),
  })
  .strict();

const describeFixtureSurface = (value: unknown): string => {
  if (value && typeof value === 'object' && 'surface' in value) {
    const surface = (value as { surface?: unknown }).surface;
    if (typeof surface === 'string' && surface.length > 0) {
      return surface;
    }
  }

  return 'unknown query capability surface';
};

const formatValidationIssues = (error: z.ZodError): string =>
  error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
      return `${path}: ${issue.message}`;
    })
    .join('; ');

export const loadQueryCapabilityFixture = (fixture: unknown): QueryCapabilityFixture => {
  const result = QueryCapabilityFixtureSchema.safeParse(fixture);

  if (!result.success) {
    throw new Error(
      `Invalid query capability fixture for ${describeFixtureSurface(fixture)}: ${formatValidationIssues(result.error)}`
    );
  }

  return result.data;
};

export const hasQueryCapabilityReason = (
  fixture: QueryCapabilityFixture,
  reason: QueryInvalidQueryReason
): boolean => fixture.invalidQueryReasons.includes(reason);
